import { NgComponentOutlet } from '@angular/common';
import { Component, type Signal, signal, Type, ViewEncapsulation } from '@angular/core';

import { createProviderToken } from '@core/common-legacy';

export interface AnyChatComponentConfig {
  name: string;
  props: Record<string, unknown>;
}

export interface AnyChatComponentProvider<
  T extends AnyChatComponentConfig = AnyChatComponentConfig,
> {
  name: T['name'];
  component: Type<{
    props: Signal<T['props']>;
  }>;
}

export const { inject: injectChatComponents, provide: provideChatComponents } =
  createProviderToken<AnyChatComponentProvider>('ChatComponent')(true);

@Component({
  selector: 'tellent-chat-component',
  encapsulation: ViewEncapsulation.ShadowDom,
  template: `
    <slot #slot style="display: none" (slotchange)="onSlotChange(slot)"></slot>
    @let config = componentConfig();
    @if (config && componentsMap[config.name]) {
      <ng-container
        [ngComponentOutlet]="componentsMap[config.name].component"
        [ngComponentOutletInputs]="{ props: config.props }"
      />
    }
  `,
  imports: [NgComponentOutlet],
  // TODO fix
  styleUrls: ['../../../../../domains/tellent/apps/tellent-client/src/styles.less'],
})
export class TellentChatComponent {
  private readonly components = injectChatComponents({ optional: true }) ?? [];

  protected readonly componentsMap = this.components.reduce(
    (map, cmp) => {
      map[cmp.name] = cmp;
      return map;
    },
    <Record<string, AnyChatComponentProvider>>{},
  );

  public componentConfig = signal<AnyChatComponentConfig | null>(null);

  protected onSlotChange(slot: HTMLSlotElement) {
    const nodes = slot.assignedNodes({ flatten: true });
    const [textNode] = nodes;
    if (!textNode) return;

    const text = textNode.textContent?.trim() || '';
    try {
      const json = JSON.parse(text);
      this.componentConfig.set(json);
    } catch (e) {
      console.error(`Failed to parse component config: "${text}"`);
      this.componentConfig.set(null);
    }
  }
}
